import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../lib/api";
import {
  Copy, Check, RefreshCw, Settings2, ChevronDown, ChevronUp,
  ArrowUpDown, ArrowUp, ArrowDown, Info, ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { API } from "../App";
import { getActiveOrgId } from "../lib/api";

// ─── Default thresholds ───────────────────────────────────────────────────────
const DEFAULT_CH = {
  enabled: true,
  umbral_escalar: 20,
  umbral_mantener: 10,
  umbral_minimo: 0,
  umbral_halo_proteger: 1.3,
  umbral_margen_alto: 30,
  umbral_margen_medio: 15,
  factor_minimo: 0.2,
};

const DECISION_COLORS = {
  escalar:        "bg-green-100 text-green-800 border border-green-200",
  mantener:       "bg-blue-100 text-blue-800 border border-blue-200",
  reducir_bid:    "bg-orange-100 text-orange-800 border border-orange-200",
  pausar:         "bg-red-100 text-red-800 border border-red-200",
  sin_publicidad: "bg-slate-100 text-slate-500 border border-slate-200",
};

const CHANNEL_META = {
  google: {
    name: "Google",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Google_%22G%22_Logo.svg/512px-Google_%22G%22_Logo.svg.png",
    color: "bg-blue-600",
    ext: ".csv",
    file: "google.csv",
    instructions: "Pega esta URL en Google Merchant Center → Productos → Feeds → Nuevo feed suplementario → URL programada",
    badge: "bg-blue-100 text-blue-800",
    note: "Actualizado cada noche a las 2:00 AM",
  },
  meta: {
    name: "Meta",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Meta_Platforms_Inc._logo.svg/800px-Meta_Platforms_Inc._logo.svg.png",
    color: "bg-indigo-600",
    ext: ".csv",
    file: "meta.csv",
    instructions: "Pega esta URL en Commerce Manager → Catálogo → Fuentes de datos → Añadir fuente → URL programada",
    badge: "bg-indigo-100 text-indigo-800",
    note: "Actualizado cada noche a las 2:00 AM",
  },
  tiktok: {
    name: "TikTok",
    logo: "",
    color: "bg-slate-900",
    ext: ".csv",
    file: "tiktok.csv",
    instructions: "Pega esta URL en TikTok Ads Manager → Assets → Catálogo → Fuente de datos → URL de feed",
    badge: "bg-slate-100 text-slate-700",
    note: "Rentabilidad global (sin gasto por SKU). Actualizado cada noche.",
  },
};

const authHeaders = () => {
  const token = localStorage.getItem("auth_token");
  const orgId = getActiveOrgId();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { "X-Org-Id": orgId } : {}),
  };
};

// ─── Threshold Form ───────────────────────────────────────────────────────────
function ThresholdForm({ channel, values, onChange, onSave, saving }) {
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Rentabilidad Real</p>
        <div className="grid grid-cols-2 gap-3">
          {[["umbral_escalar", "Escalar si >"], ["umbral_mantener", "Mantener si >"]].map(([key, lbl]) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs text-slate-600">{lbl}</Label>
              <div className="flex items-center gap-1">
                <Input type="number" min="0" step="1" value={values[key] ?? 20}
                  onChange={(e) => onChange({ ...values, [key]: parseFloat(e.target.value) || 0 })}
                  className="font-mono text-sm" data-testid={`${channel}-${key}`} />
                <span className="text-slate-400 text-sm">%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {channel !== "tiktok" && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Protección Halo</p>
          <div className="space-y-1 max-w-[180px]">
            <Label className="text-xs text-slate-600">Proteger si Halo Score &gt;</Label>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" step="0.1" value={values.umbral_halo_proteger ?? 1.3}
                onChange={(e) => onChange({ ...values, umbral_halo_proteger: parseFloat(e.target.value) || 0 })}
                className="font-mono text-sm" data-testid={`${channel}-halo`} />
              <span className="text-slate-400 text-sm">x</span>
            </div>
          </div>
        </div>
      )}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Segmentos de margen</p>
        <div className="grid grid-cols-2 gap-3">
          {[["umbral_margen_alto", "Margen alto si >"], ["umbral_margen_medio", "Margen medio si >"]].map(([key, lbl]) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs text-slate-600">{lbl}</Label>
              <div className="flex items-center gap-1">
                <Input type="number" min="0" step="1" value={values[key] ?? 30}
                  onChange={(e) => onChange({ ...values, [key]: parseFloat(e.target.value) || 0 })}
                  className="font-mono text-sm" data-testid={`${channel}-${key}`} />
                <span className="text-slate-400 text-sm">%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Button onClick={onSave} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white"
        data-testid={`save-${channel}-settings`}>
        {saving ? "Guardando..." : "Guardar umbrales"}
      </Button>
    </div>
  );
}

// ─── Classification Table ─────────────────────────────────────────────────────
function ClassificationTable({ channel, rows, loading, recalculating, onRecalculate, lastUpdated }) {
  const [sort, setSort] = useState({ key: "rent_real_pct", dir: "desc" });

  const sorted = [...(rows || [])].sort((a, b) => {
    const va = a[sort.key]; const vb = b[sort.key];
    if (va == null) return 1; if (vb == null) return -1;
    return sort.dir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const SortBtn = ({ k, children }) => (
    <button type="button" onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }))}
      className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900">
      {children}
      {sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3" />}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {lastUpdated ? `Última actualización: ${new Date(lastUpdated).toLocaleString("es-ES")}` : "Sin datos todavía"}
        </div>
        <Button size="sm" variant="outline" onClick={onRecalculate} disabled={recalculating}
          className="gap-1.5 text-xs" data-testid={`recalculate-${channel}-btn`}>
          <RefreshCw className={`w-3.5 h-3.5 ${recalculating ? "animate-spin" : ""}`} />
          {recalculating ? "Calculando..." : "Recalcular"}
        </Button>
      </div>

      {rows && rows.length === 0 && !loading && !recalculating && (
        <div className="text-center py-8 text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg">
          <p>No hay clasificaciones todavía.</p>
          <p className="text-xs mt-1">Haz clic en <strong>Recalcular</strong> para generar las clasificaciones.</p>
        </div>
      )}

      {(rows && rows.length > 0) && (
        <div className="overflow-x-auto rounded border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-left"><SortBtn k="reference">Referencia</SortBtn></TableHead>
                <TableHead className="text-left">Nombre</TableHead>
                <TableHead className="text-right"><SortBtn k="rent_real_pct">Rent. Real %</SortBtn></TableHead>
                {channel !== "tiktok" && <TableHead className="text-right"><SortBtn k="halo_score">Halo Score</SortBtn></TableHead>}
                <TableHead className="text-center">Decisión</TableHead>
                <TableHead className="text-center">Margen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.slice(0, 100).map((row, i) => (
                <TableRow key={row.product_uid || i} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs text-slate-700">{row.reference || row.product_uid || "—"}</TableCell>
                  <TableCell className="text-slate-700 text-xs max-w-[160px] truncate">{row.name || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {row.rent_real_pct != null ? (
                      <span className={row.rent_real_pct >= 20 ? "text-green-700 font-semibold" : row.rent_real_pct >= 0 ? "text-amber-600" : "text-red-600"}>
                        {Number(row.rent_real_pct).toFixed(1)}%
                      </span>
                    ) : "—"}
                  </TableCell>
                  {channel !== "tiktok" && (
                    <TableCell className="text-right font-mono text-xs text-slate-600">
                      {row.halo_score != null ? `${Number(row.halo_score).toFixed(2)}x` : "—"}
                    </TableCell>
                  )}
                  <TableCell className="text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${DECISION_COLORS[row.custom_label_0] || DECISION_COLORS.sin_publicidad}`}>
                      {row.custom_label_0 || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-xs text-slate-500">{row.custom_label_1 || "—"}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {sorted.length > 100 && (
            <p className="text-xs text-slate-400 text-center py-2">Mostrando 100 de {sorted.length} productos</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FeedOptimizerPage() {
  const [feedToken, setFeedToken] = useState(null);
  const [copiedChannel, setCopiedChannel] = useState(null);
  const [channelSettings, setChannelSettings] = useState({
    google: { ...DEFAULT_CH },
    meta:   { ...DEFAULT_CH },
    tiktok: { ...DEFAULT_CH },
  });
  const [editSettings, setEditSettings] = useState({
    google: { ...DEFAULT_CH },
    meta:   { ...DEFAULT_CH },
    tiktok: { ...DEFAULT_CH },
  });
  const [savingChannel, setSavingChannel] = useState(null);
  const [classifications, setClassifications] = useState({ google: [], meta: [], tiktok: [] });
  const [classUpdated, setClassUpdated]       = useState({ google: null, meta: null, tiktok: null });
  const [recalculating, setRecalculating]     = useState({ google: false, meta: false, tiktok: false });
  const [loadingClass, setLoadingClass]       = useState({ google: false, meta: false, tiktok: false });
  const [activeTab, setActiveTab]             = useState("google");
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const feedUrl = (channel) =>
    feedToken ? `${API}/public/feed/${feedToken}/${CHANNEL_META[channel].file}` : null;

  const copyUrl = (channel) => {
    const url = feedUrl(channel);
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedChannel(channel);
      toast.success(`URL de ${CHANNEL_META[channel].name} copiada`);
      setTimeout(() => setCopiedChannel(null), 2500);
    });
  };

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feed-optimizer/v2/settings`, {
        withCredentials: true, headers: authHeaders(),
      });
      const s = res.data;
      setFeedToken(s.merchant_feed_token || null);
      const ch = s.channels || {};
      const merged = {
        google: { ...DEFAULT_CH, ...(ch.google || {}) },
        meta:   { ...DEFAULT_CH, ...(ch.meta   || {}) },
        tiktok: { ...DEFAULT_CH, ...(ch.tiktok  || {}) },
      };
      setChannelSettings(merged);
      setEditSettings(JSON.parse(JSON.stringify(merged)));
    } catch {}
  }, []);

  // Load classifications for a channel
  const loadClassifications = useCallback(async (channel) => {
    setLoadingClass((prev) => ({ ...prev, [channel]: true }));
    try {
      const res = await axios.get(`${API}/feed-optimizer/v2/classifications/${channel}`, {
        withCredentials: true, headers: authHeaders(),
      });
      setClassifications((prev) => ({ ...prev, [channel]: res.data.rows || [] }));
      setClassUpdated((prev) => ({ ...prev, [channel]: res.data.updated_at || null }));
    } catch {} finally {
      setLoadingClass((prev) => ({ ...prev, [channel]: false }));
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadClassifications(activeTab); }, [activeTab, loadClassifications]);

  const saveChannelSettings = async (channel) => {
    setSavingChannel(channel);
    try {
      await axios.put(`${API}/feed-optimizer/v2/settings/${channel}`, editSettings[channel], {
        withCredentials: true, headers: authHeaders(),
      });
      setChannelSettings((prev) => ({ ...prev, [channel]: editSettings[channel] }));
      toast.success(`Umbrales de ${CHANNEL_META[channel].name} guardados`);
    } catch {
      toast.error("Error guardando configuración");
    } finally {
      setSavingChannel(null);
    }
  };

  const recalculate = async (channel) => {
    setRecalculating((prev) => ({ ...prev, [channel]: true }));
    try {
      await axios.post(`${API}/feed-optimizer/v2/recalculate/${channel}`, {}, {
        withCredentials: true, headers: authHeaders(),
      });
      toast.info(`Recalculando clasificaciones para ${CHANNEL_META[channel].name}...`);
      // Poll until done (max 3 min)
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        try {
          const res = await axios.get(`${API}/feed-optimizer/v2/classifications/${channel}`, {
            withCredentials: true, headers: authHeaders(),
          });
          if (res.data.rows && res.data.rows.length > 0) {
            setClassifications((prev) => ({ ...prev, [channel]: res.data.rows }));
            setClassUpdated((prev) => ({ ...prev, [channel]: res.data.updated_at }));
            clearInterval(interval);
            setRecalculating((prev) => ({ ...prev, [channel]: false }));
            toast.success(`Clasificaciones de ${CHANNEL_META[channel].name} actualizadas`);
          }
        } catch {}
        if (attempts >= 36) {
          clearInterval(interval);
          setRecalculating((prev) => ({ ...prev, [channel]: false }));
        }
      }, 5000);
    } catch {
      toast.error("Error iniciando recálculo");
      setRecalculating((prev) => ({ ...prev, [channel]: false }));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="feed-optimizer-page">

        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <Settings2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Feed Optimizer</h1>
            <p className="text-sm text-slate-500">Feeds multicanal para Google Merchant Center, Meta Commerce y TikTok Ads · Clasificación basada en rentabilidad real</p>
          </div>
        </div>

        {/* ── SECTION 1: Feed URLs ─────────────────────────────────────────── */}
        <div>
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">URLs de Feeds</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(["google", "meta", "tiktok"]).map((ch) => {
              const meta = CHANNEL_META[ch];
              const url  = feedUrl(ch);
              const copied = copiedChannel === ch;
              return (
                <Card key={ch} className="bg-white border border-slate-200" data-testid={`feed-card-${ch}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 ${meta.color} rounded-lg flex items-center justify-center`}>
                          <span className="text-white text-xs font-bold">{meta.name[0]}</span>
                        </div>
                        <CardTitle className="text-sm font-bold text-slate-900">{meta.name}</CardTitle>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.badge}`}>
                        {url ? "Activo" : "Generando..."}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {url ? (
                      <>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-slate-600 break-all leading-relaxed" data-testid={`feed-url-${ch}`}>
                            {url}
                          </code>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => copyUrl(ch)}
                          className="w-full gap-1.5 text-xs" data-testid={`copy-feed-${ch}-btn`}>
                          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                          {copied ? "Copiado" : "Copiar URL"}
                        </Button>
                        <div className="rounded bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-600">
                          <p className="font-medium mb-1">{meta.instructions}</p>
                          <p className="text-slate-400">{meta.note}</p>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-slate-400 text-center py-3">Generando token...</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* ── SECTION 2: Custom Labels por canal ───────────────────────────── */}
        <div>
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Custom Labels por Canal</h2>
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="feed-channel-tabs">
                <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-transparent px-4 pt-4 pb-0 h-auto gap-1">
                  {["google", "meta", "tiktok"].map((ch) => (
                    <TabsTrigger key={ch} value={ch}
                      className="px-4 py-2 text-sm font-medium rounded-t-lg data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-b-white data-[state=active]:text-slate-900 text-slate-500"
                      data-testid={`tab-${ch}`}>
                      {CHANNEL_META[ch].name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {["google", "meta", "tiktok"].map((ch) => (
                  <TabsContent key={ch} value={ch} className="p-5 space-y-5">
                    {/* Channel description */}
                    <div className="rounded bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                      {ch === "google" && "Clasificación basada en Rentabilidad Real calculada con gasto de Google Ads API + conversiones de GA4 (source=google/cpc)."}
                      {ch === "meta"   && "Clasificación basada en Rentabilidad Real calculada con gasto de Meta Insights API + conversiones GA4 (source=facebook/instagram). Si no hay campañas de catálogo activas, se usa la rentabilidad global."}
                      {ch === "tiktok" && "Clasificación basada en rentabilidad global del producto (margen bruto desde pedidos reales). TikTok no expone gasto por SKU para campañas estándar de catálogo."}
                    </div>

                    {/* Threshold settings (collapsible) */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setSettingsExpanded(!settingsExpanded)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700"
                        data-testid={`toggle-settings-${ch}`}
                      >
                        <span className="flex items-center gap-2">
                          <Settings2 className="w-4 h-4" />
                          Configuración de umbrales
                        </span>
                        {settingsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {settingsExpanded && (
                        <div className="p-4">
                          <ThresholdForm
                            channel={ch}
                            values={editSettings[ch] || DEFAULT_CH}
                            onChange={(v) => setEditSettings((prev) => ({ ...prev, [ch]: v }))}
                            onSave={() => saveChannelSettings(ch)}
                            saving={savingChannel === ch}
                          />
                        </div>
                      )}
                    </div>

                    {/* Classification table */}
                    <ClassificationTable
                      channel={ch}
                      rows={classifications[ch]}
                      loading={loadingClass[ch]}
                      recalculating={recalculating[ch]}
                      onRecalculate={() => recalculate(ch)}
                      lastUpdated={classUpdated[ch]}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* ── SECTION 3: Category Optimization (Coming Soon) ───────────────── */}
        <div>
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Optimización de Categorías</h2>
          <Card className="bg-white border border-slate-200 opacity-60">
            <CardContent className="p-6 text-center">
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Info className="w-5 h-5 text-slate-400" />
              </div>
              <p className="font-semibold text-slate-700">Próximamente</p>
              <p className="text-sm text-slate-500 mt-1">
                La optimización de <code>google_product_category</code> con IA (Claude) se añadirá en la siguiente fase.
                Asignará automáticamente la categoría más específica de la taxonomía de Google a cada producto.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Link to Google Ads */}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <ExternalLink className="w-4 h-4" />
          <Link to="/analytics/google-ads-products" className="hover:text-slate-900 underline">
            Ver tabla de productos en Google Ads
          </Link>
          <span>·</span>
          <Link to="/analytics/meta-ads-products" className="hover:text-slate-900 underline">
            Ver tabla de productos en Meta Ads
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
